import { Alert, Button, Checkbox, Col, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Skeleton, Space, Tag, Typography, App as AntApp } from 'antd';
import { ArrowRightOutlined, DeleteOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useApartment,
  useDeleteApartment,
  useTransitionApartment,
  useUpdateApartment,
  type ActiveReservation,
  type Apartment,
  type ApartmentLayout,
  type ApartmentStatus,
  type PaymentMethod,
} from '../api/apartments';
import { useContract } from '@/features/contracts/api/contracts';
import { useCustomersList } from '@/features/customers/api/customers';
import { formatUzs, pricePerSqm } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';
import { RoleGate, AGENT_ROLES } from '@/shared/auth/role';

const PAYMENT_METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'card'];

function formatTimeLeft(expiresAt: string): { text: string; expired: boolean } {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return { text: '', expired: true };
  const totalMin = Math.floor(diff / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin - days * 60 * 24) / 60);
  const mins = totalMin - days * 60 * 24 - hours * 60;
  if (days > 0) return { text: `${days}д ${hours}ч`, expired: false };
  if (hours > 0) return { text: `${hours}ч ${mins}м`, expired: false };
  if (mins > 0) return { text: `${mins}м`, expired: false };
  return { text: '<1м', expired: false };
}

function TimeLeft({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(i);
  }, []);
  // tick triggers re-render; recompute every render so first render is fresh.
  void tick;
  const { text, expired } = formatTimeLeft(expiresAt);
  if (expired) {
    return <Tag color="red">{t('apartments.expired')}</Tag>;
  }
  return (
    <Typography.Text strong style={{ color: '#16a34a' }}>
      {t('apartments.expiresIn')}: {text}
    </Typography.Text>
  );
}

function ActiveReservationCard({ reservation }: { reservation: ActiveReservation }) {
  const { t } = useTranslation();
  const cust = reservation.customer;
  return (
    <div
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('apartments.customer')}
          </Typography.Text>
          <div>
            <Link to={`/customers/${cust.id}`}>
              <Typography.Text strong>
                {cust.lastName} {cust.firstName}
              </Typography.Text>
            </Link>
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              {cust.phone}
            </Typography.Text>
          </div>
        </div>

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('apartments.reservedBy')}:
          </Typography.Text>{' '}
          <Typography.Text>{reservation.createdByUser.fullName}</Typography.Text>
        </div>

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('apartments.reservedUntil')}:
          </Typography.Text>{' '}
          <Typography.Text>{dayjs(reservation.expiresAt).format('DD.MM.YYYY HH:mm')}</Typography.Text>
        </div>

        <TimeLeft expiresAt={reservation.expiresAt} />

        {reservation.reservationFee && reservation.paymentMethod && (
          <div
            style={{
              marginTop: 4,
              paddingTop: 8,
              borderTop: '1px dashed #fde68a',
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('apartments.depositReceived')}
            </Typography.Text>
            <div>
              <Typography.Text strong>{formatUzs(reservation.reservationFee)}</Typography.Text>
              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                · {t(`apartments.paymentMethods.${reservation.paymentMethod}`)}
              </Typography.Text>
            </div>
            {reservation.paymentRef && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('apartments.paymentRef')}: {reservation.paymentRef}
              </Typography.Text>
            )}
          </div>
        )}
      </Space>
    </div>
  );
}

const CONTRACT_STATUS_COLOR: Record<string, string> = {
  draft: 'default',
  active: 'green',
  completed: 'blue',
  cancelled: 'red',
  defaulted: 'volcano',
};

/** Compact card summarising the contract that locks this apartment. */
function ContractInfoCard({ contractId }: { contractId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: contract, isLoading } = useContract(contractId);

  if (isLoading) {
    return (
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </div>
    );
  }
  if (!contract) return null;

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to={`/contracts/${contract.id}`}>
            <Typography.Text strong style={{ fontSize: 14 }}>
              <FileTextOutlined style={{ marginRight: 6 }} />
              {contract.contractNumber}
            </Typography.Text>
          </Link>
          <Tag color={CONTRACT_STATUS_COLOR[contract.status]} style={{ margin: 0 }}>
            {t(`contracts.status.${contract.status}`)}
          </Tag>
        </div>

        {contract.customer && (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('contracts.customer')}:
            </Typography.Text>{' '}
            <Link to={`/customers/${contract.customer.id}`}>
              <Typography.Text>
                {contract.customer.lastName} {contract.customer.firstName}
              </Typography.Text>
            </Link>
          </div>
        )}

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('contracts.totalAmount')}:
          </Typography.Text>{' '}
          <Typography.Text strong>{formatUzs(contract.totalAmount)}</Typography.Text>
        </div>

        <Button
          type="primary"
          ghost
          block
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/contracts/${contract.id}`)}
        >
          {t('apartments.viewContract')}
        </Button>
      </Space>
    </div>
  );
}

/** Compact key/value chip for the apartment stats row. */
function StatPill({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '6px 12px',
        minWidth: 64,
        textAlign: 'center',
      }}
    >
      <Typography.Text strong style={{ display: 'block', fontSize: 15 }}>
        {value}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {label}
      </Typography.Text>
    </div>
  );
}

const LAYOUTS: ApartmentLayout[] = [
  'studio',
  'one_room',
  'two_room',
  'three_room',
  'four_room_plus',
  'penthouse',
  'commercial',
];

const STATUS_COLOR: Record<ApartmentStatus, string> = {
  available: 'green',
  reserved: 'gold',
  contracted: 'red',
  sold: 'volcano',
  delivered: 'blue',
  cancelled: 'default',
};

type Props = {
  apartmentId: string | null;
  projectId: string;
  onClose: () => void;
};

export function ApartmentDrawer({ apartmentId, projectId, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const { data: apt, isLoading } = useApartment(apartmentId ?? undefined);
  const transition = useTransitionApartment(projectId);
  const del = useDeleteApartment(projectId);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function handleRelease() {
    if (!apt) return;
    try {
      await transition.mutateAsync({ id: apt.id, input: { action: 'release' } });
      void message.success(t('apartments.released'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function handleDelete() {
    if (!apt) return;
    try {
      await del.mutateAsync(apt.id);
      void message.success(t('apartments.deleted'));
      onClose();
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <>
      <Drawer
        open={!!apartmentId}
        onClose={onClose}
        width={420}
        title={apt ? apt.fullCode : t('common.loading')}
        loading={isLoading}
        destroyOnHidden
        extra={
          apt && (
            <Space size={4}>
              {apt.status !== 'contracted' && apt.status !== 'sold' && apt.status !== 'delivered' && (
                <Button size="small" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                  {t('common.edit')}
                </Button>
              )}
              {apt.status === 'available' && (
                <Popconfirm
                  title={t('apartments.deleteConfirmTitle')}
                  description={t('apartments.deleteConfirmBody')}
                  okText={t('common.delete')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ danger: true, loading: del.isPending }}
                  onConfirm={handleDelete}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </Space>
          )
        }
      >
        {apt && (
          <Space direction="vertical" size={18} style={{ width: '100%' }}>
            {/* Status + location */}
            <div>
              <Tag color={STATUS_COLOR[apt.status]} style={{ fontSize: 12, padding: '2px 10px' }}>
                {t(`apartments.status.${apt.status}`)}
              </Tag>
              {(apt.block || apt.floor) && (
                <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                  {apt.block ? `${t('apartments.block_')} ${apt.block.name || apt.block.code}` : ''}
                  {apt.block && apt.floor ? ' · ' : ''}
                  {apt.floor ? `${apt.floor.number}-${t('apartments.floor').toLowerCase()}` : ''}
                </Typography.Text>
              )}
            </div>

            {/* Layout */}
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('apartments.layout_')}</Typography.Text>
              <div>
                <Typography.Text strong style={{ fontSize: 15 }}>
                  {t(`apartments.layout.${apt.layoutType}`)}
                </Typography.Text>
              </div>
            </div>

            {/* Compact stats row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatPill label={t('apartments.area')} value={`${Number(apt.areaSqm)} м²`} />
              <StatPill label={t('apartments.rooms')} value={apt.rooms} />
              <StatPill label={t('apartments.bathrooms')} value={apt.bathrooms} />
              <StatPill label={t('apartments.balconies')} value={apt.balconies} />
            </div>

            {/* Price */}
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('apartments.price')}
              </Typography.Text>
              <Typography.Title level={3} style={{ margin: '4px 0 0' }}>
                {formatUzs(apt.basePrice)}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {pricePerSqm(apt.basePrice, apt.areaSqm)}
              </Typography.Text>
            </div>

            {/* Status-specific actions */}
            <div style={{ paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              {apt.status === 'available' && (
                <RoleGate roles={AGENT_ROLES} fallback={<Alert type="info" showIcon message={t('apartments.status.available')} />}>
                  <Button type="primary" block size="large" onClick={() => setReserveOpen(true)}>
                    {t('apartments.reserve')}
                  </Button>
                </RoleGate>
              )}

              {apt.status === 'reserved' && (
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {apt.currentReservation ? (
                    <ActiveReservationCard reservation={apt.currentReservation} />
                  ) : (
                    <Alert type="warning" showIcon message={t('apartments.reservedHint')} />
                  )}
                  <RoleGate roles={AGENT_ROLES}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        block
                        icon={<FileTextOutlined />}
                        onClick={() => navigate(`/contracts/new?apartmentId=${apt.id}`)}
                      >
                        {t('apartments.createContract')}
                      </Button>
                      <Button danger block onClick={handleRelease} loading={transition.isPending}>
                        {t('apartments.release')}
                      </Button>
                    </Space>
                  </RoleGate>
                </Space>
              )}

              {(apt.status === 'contracted' || apt.status === 'sold' || apt.status === 'delivered') && (
                apt.currentContractId ? (
                  <ContractInfoCard contractId={apt.currentContractId} />
                ) : (
                  <Alert type="info" showIcon message={t(`apartments.status.${apt.status}`)} />
                )
              )}

              {apt.status === 'cancelled' && (
                <Alert type="warning" showIcon message={t('apartments.status.cancelled')} />
              )}
            </div>
          </Space>
        )}
      </Drawer>

      {apt && (
        <EditApartmentModal
          open={editOpen}
          apartment={apt}
          projectId={projectId}
          onClose={() => setEditOpen(false)}
        />
      )}

      {apt && (
        <ReserveModal
          open={reserveOpen}
          apartmentId={apt.id}
          projectId={projectId}
          onClose={() => setReserveOpen(false)}
        />
      )}
    </>
  );
}

type ReserveFormValues = {
  customerId: string;
  days: number;
  collectPayment: boolean;
  paymentAmount?: number; // raw UZS, no tiyin
  paymentMethod?: PaymentMethod;
  paymentRef?: string;
};

function ReserveModal({
  open,
  apartmentId,
  projectId,
  onClose,
}: {
  open: boolean;
  apartmentId: string;
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<ReserveFormValues>();
  const [search, setSearch] = useState('');
  const { data: customers, isLoading } = useCustomersList({ search: search.length >= 2 ? search : undefined });
  const transition = useTransitionApartment(projectId);
  const collectPayment = Form.useWatch('collectPayment', form) ?? false;

  async function onOk() {
    try {
      const values = await form.validateFields();
      const input: Parameters<typeof transition.mutateAsync>[0]['input'] = {
        action: 'reserve',
        customerId: values.customerId,
        hours: Number(values.days) * 24,
      };
      if (values.collectPayment && values.paymentAmount && values.paymentMethod) {
        input.reservationFee = String(BigInt(Math.round(Number(values.paymentAmount) * 100)));
        input.paymentMethod = values.paymentMethod;
        if (values.paymentRef) input.paymentRef = values.paymentRef;
      }
      await transition.mutateAsync({ id: apartmentId, input });
      void message.success(t('apartments.reservedSuccess'));
      onClose();
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={t('apartments.reserveTitle')}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={transition.isPending}
      okText={t('apartments.reserve')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ days: 1, collectPayment: false }}
      >
        <Form.Item name="customerId" label={t('apartments.customer')} rules={[{ required: true }]}>
          <Select
            showSearch
            placeholder={t('apartments.customerPlaceholder')}
            filterOption={false}
            onSearch={setSearch}
            loading={isLoading}
            options={(customers?.data ?? []).map((c) => ({
              value: c.id,
              label: `${c.lastName} ${c.firstName} · ${c.phone}`,
            }))}
            notFoundContent={search.length < 2 ? t('apartments.customerTypeHint') : t('apartments.customerNotFound')}
          />
        </Form.Item>

        <Form.Item
          name="days"
          label={t('apartments.days')}
          rules={[{ required: true, type: 'number', min: 1, max: 30 }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={30}
            addonAfter={t('apartments.daysUnit')}
          />
        </Form.Item>

        <Form.Item name="collectPayment" valuePropName="checked" style={{ marginBottom: 8 }}>
          <Checkbox>{t('apartments.collectPayment')}</Checkbox>
        </Form.Item>

        {collectPayment && (
          <div
            style={{
              background: '#f8fafc',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          >
            <Row gutter={12}>
              <Col xs={24} md={14}>
                <Form.Item
                  name="paymentAmount"
                  label={t('apartments.paymentAmount')}
                  rules={[{ required: true, type: 'number', min: 1 }]}
                  style={{ marginBottom: 12 }}
                >
                  <InputNumber<number>
                    style={{ width: '100%' }}
                    min={0}
                    step={100000}
                    formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                    addonAfter="UZS"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="paymentMethod"
                  label={t('apartments.paymentMethod')}
                  rules={[{ required: true }]}
                  style={{ marginBottom: 12 }}
                >
                  <Select
                    options={PAYMENT_METHODS.map((m) => ({
                      value: m,
                      label: t(`apartments.paymentMethods.${m}`),
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="paymentRef" label={t('apartments.paymentRef')} style={{ marginBottom: 0 }}>
              <Input maxLength={120} />
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
}

function EditApartmentModal({
  open,
  apartment,
  projectId,
  onClose,
}: {
  open: boolean;
  apartment: Apartment;
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const update = useUpdateApartment(projectId);
  const [form] = Form.useForm();

  async function onOk() {
    try {
      const values = await form.validateFields();
      await update.mutateAsync({
        id: apartment.id,
        input: {
          number: values.number,
          layoutType: values.layoutType,
          areaSqm: Number(values.areaSqm),
          rooms: Number(values.rooms),
          bathrooms: Number(values.bathrooms),
          balconies: Number(values.balconies),
          basePrice: String(BigInt(Math.round(Number(values.basePrice) * 100))),
        },
      });
      void message.success(t('apartments.updated'));
      onClose();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={t('apartments.editTitle')}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={update.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      width={620}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          number: apartment.number,
          layoutType: apartment.layoutType,
          areaSqm: Number(apartment.areaSqm),
          rooms: apartment.rooms,
          bathrooms: apartment.bathrooms,
          balconies: apartment.balconies,
          // Convert tiyin → UZS for editing.
          basePrice: Number(BigInt(apartment.basePrice) / 100n),
        }}
      >
        <Row gutter={12}>
          <Col xs={12} md={6}>
            <Form.Item name="number" label="№" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} stringMode />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item name="layoutType" label={t('apartments.layout_')} rules={[{ required: true }]}>
              <Select
                options={LAYOUTS.map((l) => ({ value: l, label: t(`apartments.layout.${l}`) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="areaSqm" label={t('apartments.area')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={1} max={500} addonAfter="м²" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={8}>
            <Form.Item name="rooms" label={t('apartments.rooms')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={20} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item name="bathrooms" label={t('apartments.bathrooms')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={10} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item name="balconies" label={t('apartments.balconies')} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={10} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="basePrice" label={t('apartments.price')} rules={[{ required: true }]}>
          <InputNumber<number>
            style={{ width: '100%' }}
            min={0}
            step={100000}
            formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
            addonAfter="UZS"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
